import * as stylex from "@stylexjs/stylex";
import { Info, LogOut, type LucideIcon, Monitor, QrCode, Smartphone, Tablet, Trash2 } from "lucide-react";
import { copy } from "@/copy";
import type { Account } from "@/domain/schemas";
import { activeStamp, type DeviceKind, type SignedInScreen } from "@/domain/screens";
import type { PhotoUsage } from "@/domain/storage";
import "@/theme/seasons.stylex";
import { season } from "@/theme/seasons.stylex";
import "@/theme/tokens.stylex";
import { colors, motion, radii, shadows, space } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { Button } from "@/ui/Button";
import { Eyebrow } from "@/ui/Eyebrow";
import { Icon } from "@/ui/Icon";
import { QrSignInTile, type QrTileShown } from "./QrSignInTile";
import { StorageMeter } from "./StorageMeter";
export type SettingsAccount = Pick<Account, "name" | "email">;
type AccountCardProps = {
  account: SettingsAccount;
  screens: readonly SignedInScreen[];
  storage: PhotoUsage | undefined;
  now: Date;
  tz: string;
  online: boolean;
  qr: QrTileShown;
  onShowQr: () => void;
  onCloseQr: () => void;
  onSignOut: () => void;
  onDeleteAccount: () => void;
};
const DEVICE_ICONS: Record<DeviceKind, LucideIcon> = {
  monitor: Monitor,
  tablet: Tablet,
  smartphone: Smartphone
};
const text = copy.settings.account;
const styles = {
  button: {
    kGVxlE: "xnfxhx2",
    $$css: true
  },
  danger: {
    kMwMTN: "x3258oq",
    $$css: true
  }
};
export function AccountCard({
  account,
  screens,
  storage,
  now,
  tz,
  online,
  qr,
  onShowQr,
  onCloseQr,
  onSignOut,
  onDeleteAccount
}: AccountCardProps) {
  const shownName = account.name ?? account.email;
  const count = text.screensSignedIn(screens.length);
  return <section aria-label={text.title} className="x78zum5 xdt5ytf x1c1vhfx xh540kj xwe7p7i xnfxhx2 xgkd4oa x9f619">
      <Eyebrow>{text.title}</Eyebrow>
      <div className="x78zum5 x6s0dn4 xddi9bj">
        <span aria-hidden className="x15yg21f xnnlda6 x2lah0s xqhrb7l x14rdugl xrvj5dj x1ku5rj1 xwsyq91 xfbjfs5 x15406qy x11l8gmq x1eyvnp3">
          {shownName.charAt(0)}
        </span>
        <div className="x78zum5 xdt5ytf x195vfkc xeuugli">
          <span className="x1iig92y xhxcbl2 x1hrmo45 xb3r6kr xlyipyv">{shownName}</span>
          {account.name !== null && <span className="x14c3fy2 x1dld7ex xb3r6kr xlyipyv">{account.email}</span>}
        </div>
      </div>
      {storage && online && <StorageMeter usage={storage} />}
      <div className="x78zum5 xdt5ytf x1cy8zhl x152posx">
        <Eyebrow>{count}</Eyebrow>
        <ul aria-label={count} className="xkh2ocl x78zum5 xdt5ytf x1ghz6dp x674116 xe8uvvx x5r8ks9 x1fv5t7x">
          {screens.map((screen, index) => <ScreenRow key={screen.id} screen={screen} divided={index > 0} now={now} tz={tz} />)}
        </ul>
        <QrSignIn email={account.email} online={online} qr={qr} onShowQr={onShowQr} onCloseQr={onCloseQr} />
      </div>
      <div className="x78zum5 x1a02dak x6s0dn4 x1qughib x9snybx x7beud5 x1jidhvw">
        <div className="x78zum5 x6s0dn4 x152posx xeuugli">
          <span className="x78zum5 x2lah0s">
            <Icon icon={Info} size={18} />
          </span>
          <Eyebrow size="caption">{text.signOutCaption}</Eyebrow>
        </div>
        <Button variant="secondary" icon={LogOut} onClick={onSignOut} xstyle={styles.button}>
          {text.signOut}
        </Button>
      </div>
      <div className="x78zum5 x1a02dak x6s0dn4 x1qughib x9snybx x7beud5 x1jidhvw">
        <div className="x78zum5 xdt5ytf x1jnr06f xeuugli x1iyjqo2 xs83m0k x5z4jtk">
          <span className="x19d36u7 xfbjfs5 x1j7pf75">{text.deleteAccount.title}</span>
          <span className="x1jvydc1 x15i68ds x37zpob x1fzhlzt">{text.deleteAccount.caption}</span>
        </div>
        <Button variant="secondary" icon={Trash2} disabled={!online} onClick={onDeleteAccount} xstyle={[styles.button, styles.danger]}>
          {text.deleteAccount.title}
        </Button>
      </div>
    </section>;
}
function screenCaption(screen: SignedInScreen, now: Date, tz: string): string {
  return screen.current ? text.thisScreen : text.active(activeStamp(screen.lastSeenAt, now, tz));
}
function ScreenRow({
  screen,
  divided,
  now,
  tz
}: {
  screen: SignedInScreen;
  divided: boolean;
  now: Date;
  tz: string;
}) {
  return <li {...{
    0: {
      className: "x78zum5 x6s0dn4 xddi9bj x8nclml x1b4vry6 x9f619"
    },
    1: {
      className: "x78zum5 x6s0dn4 xddi9bj x8nclml x1b4vry6 x9f619 x1jidhvw"
    }
  }[!!divided << 0]}>
      <span className="x100vrsf x1vqgdyp x2lah0s xqhrb7l xh540kj xrvj5dj x1ku5rj1">
        <Icon icon={DEVICE_ICONS[screen.kind]} size={20} label={text.deviceKind(screen.kind)} />
      </span>
      <div className="x78zum5 xdt5ytf x195vfkc x1iyjqo2 xeuugli">
        <span className="x19d36u7 xfbjfs5 x1j7pf75 xb3r6kr xlyipyv xuxw1ft">{text.screenName(screen)}</span>
        <Eyebrow size="caption">{screenCaption(screen, now, tz)}</Eyebrow>
      </div>
      {screen.current && <span aria-hidden className="x1fsd2vl x170jfvy x2lah0s xqhrb7l x7ns719" />}
    </li>;
}
function QrSignIn({
  email,
  online,
  qr,
  onShowQr,
  onCloseQr
}: Pick<AccountCardProps, "online" | "qr" | "onShowQr" | "onCloseQr"> & {
  email: string;
}) {
  if (qr.tile.kind === "closed") {
    return <Button variant="secondary" icon={QrCode} disabled={!online} onClick={onShowQr} xstyle={styles.button}>
        {text.qr.show}
      </Button>;
  }
  return <QrSignInTile {...qr} email={email} online={online} onRetry={onShowQr} onClose={onCloseQr} />;
}